import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import { IManagedDbForeignKey, IManagedDbInboundForeignKey } from "../../../services/table-editor-service";

/**
 * What a table's columns are keys of, and what the table is tied to.
 *
 * The columns editor showed neither, so a missing primary key only surfaced later as Browse
 * Data's "Add a primary key to the table, then reopen Browse Data", and a foreign key was
 * visible nowhere but the ER diagram. The managed-DB row query answers with all of it —
 * `primaryKeyColumns`, `foreignKeys`, `inboundForeignKeys` — so this is the same data the row
 * editor already draws its FK picker from, read one page at a time.
 */

export interface ITableColumnKey {
  isPk: boolean;
  /** Where a foreign key points: `Customers.Id`, or just `Customers` when the column is unknown. */
  referencesLabel?: string;
}

export interface ITableReference {
  column: string;
  target: string;
}

export interface ITableRelations {
  /** This table's own foreign keys. */
  references: ITableReference[];
  /** Columns in other tables pointing here, as `OrderLines.OrderId`. */
  referencedBy: string[];
}

const lower = (value: string | undefined): string => (value ?? "").toLowerCase();

const targetLabel = (fk: IManagedDbForeignKey): string =>
  fk.refColumnName ? `${fk.refTableName}.${fk.refColumnName}` : fk.refTableName;

/**
 * Keyed by column name in lower case — SQL names reach these two lists in whatever case each
 * surface used.
 */
export const buildColumnKeys = (
  columns: IDatabaseColumnModel[],
  foreignKeys: IManagedDbForeignKey[],
  primaryKeyColumns: string[]
): Map<string, ITableColumnKey> => {
  const keys: Map<string, ITableColumnKey> = new Map();
  const pkNames: Set<string> = new Set((primaryKeyColumns ?? []).map(lower));

  (columns ?? []).forEach((column: IDatabaseColumnModel): void => {
    // `isPk` on the column and the server's `primaryKeyColumns` are two readings of one fact;
    // either is enough to mark the row.
    if (column.isPk === true || pkNames.has(lower(column.name))) {
      keys.set(lower(column.name), { isPk: true });
    }
  });

  (foreignKeys ?? []).forEach((fk: IManagedDbForeignKey): void => {
    const existing: ITableColumnKey | undefined = keys.get(lower(fk.columnName));
    keys.set(lower(fk.columnName), {
      isPk: existing?.isPk === true,
      referencesLabel: targetLabel(fk)
    });
  });

  return keys;
};

/** Both directions, so the editor can say what a change here would reach. */
export const buildTableRelations = (
  foreignKeys: IManagedDbForeignKey[],
  inboundForeignKeys: IManagedDbInboundForeignKey[]
): ITableRelations => {
  const references: ITableReference[] = (foreignKeys ?? []).map((fk: IManagedDbForeignKey): ITableReference => ({
    column: fk.columnName,
    target: targetLabel(fk)
  }));

  const referencedBy: string[] = (inboundForeignKeys ?? []).map(
    (fk: IManagedDbInboundForeignKey): string => `${fk.fromTableName}.${fk.fromColumnName}`
  );

  return { references, referencedBy: [...new Set(referencedBy)] };
};
